import React, { useState } from 'react';
import { useData } from '../../data/DataProvider';
import { useToast } from '../../shared/components/ToastProvider';
import KanbanBoard from './KanbanBoard';
import ScheduleCalendar from './ScheduleCalendar';
import JobsMap from './JobsMap';
import JobForm from '../jobs/JobForm';
import { exportCsv, COLUMNS } from '../../shared/importExport';
import SafeIcon from '../../common/SafeIcon';
import { Icons } from '../../shared/icons';
import EmptyState from '../../shared/components/EmptyState';

const STATUS_OPTIONS = [
  ['all', 'All statuses'],
  ['scheduled', 'Scheduled'],
  ['in_progress', 'In progress'],
  ['completed', 'Completed'],
  ['requested', 'Requested by client'],
  ['reschedule_requested', 'Reschedule requested'],
];

const isoDay = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

// Schedule (E8 + Pass 4 §3/§4): Kanban board (drag between statuses),
// FullCalendar (drag/resize/click-to-edit), and a jobs map (status
// colour-coded, default next-30-days window). Filtering is shared across
// views; the calendar and map edit jobs IN PLACE via JobForm (drawer).
export default function Schedule() {
  const { data, update } = useData();
  const { notify } = useToast();
  const [view, setView] = useState('kanban');
  const [query, setQuery] = useState('');
  const [engineer, setEngineer] = useState('all'); // engineer_id
  const [status, setStatus] = useState('all');
  const [editingJob, setEditingJob] = useState(null);

  // Map date window — default next 30 days from today.
  const today = new Date();
  const [range, setRange] = useState({
    from: isoDay(today),
    to: isoDay(new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000)),
  });

  const clientName = (clientId) => data.clients.find((item) => item.id === clientId)?.name || 'Unknown client';

  const filtered = data.jobs.filter((job) => {
    const client = data.clients.find((item) => item.id === job.client_id);
    const haystack = `${job.title} ${client?.name || ''} ${job.engineer || ''}`.toLowerCase();
    if (query && !haystack.includes(query.toLowerCase())) return false;
    if (engineer !== 'all' && job.engineer_id !== engineer) return false;
    if (status !== 'all' && job.status !== status) return false;
    return true;
  });

  // The map additionally honours the date window (scheduled_start).
  const mapJobs = filtered.filter((job) => {
    if (!job.scheduled_start) return true;
    const day = job.scheduled_start.slice(0, 10);
    return day >= range.from && day <= range.to;
  });

  const moveJob = async (job, nextStatus) => {
    try {
      await update('jobs', job.id, {
        ...job,
        status: nextStatus,
        date_completed: nextStatus === 'completed' ? (job.date_completed || new Date().toISOString().slice(0, 10)) : '',
      });
      notify(`"${job.title}" moved to ${nextStatus.replace('_', ' ')}`);
    } catch (err) {
      notify(err.message || 'Could not move the job', 'error');
    }
  };

  const inProgress = data.jobs.filter((job) => job.status === 'in_progress').length;
  const scheduled = data.jobs.filter((job) => job.status === 'scheduled').length;
  const requests = data.jobs.filter((job) => job.status === 'requested' || job.status === 'reschedule_requested');

  return (
    <div className="standalone-page schedule-page">
      <header className="simple-header">
        <div>
          <span className="eyebrow">Planning</span>
          <h1>Schedule</h1>
          <p className="field-hint">
            {scheduled} scheduled · {inProgress} in progress{requests.length ? ` · ${requests.length} client request${requests.length > 1 ? 's' : ''}` : ''} — drag cards or calendar events to update.
          </p>
        </div>
        <div className="header-actions">
          <button
            className="icon-button"
            title="Export filtered jobs (CSV)"
            aria-label="Export CSV"
            onClick={() => {
              exportCsv('jobs', filtered, COLUMNS.jobs.map((col) => ({ label: col.label, value: (row) => col.value(row, data) })));
              notify('CSV exported');
            }}
          >
            <SafeIcon icon={Icons.Download} />
          </button>
          <div className="filter-chips">
            <button className={`chip ${view === 'kanban' ? 'chip-active' : ''}`} onClick={() => setView('kanban')}>Board</button>
            <button className={`chip ${view === 'calendar' ? 'chip-active' : ''}`} onClick={() => setView('calendar')}>Calendar</button>
            <button className={`chip ${view === 'map' ? 'chip-active' : ''}`} onClick={() => setView('map')}>Map</button>
          </div>
        </div>
      </header>

      <div className="toolbar">
        <label className="search-input">
          <SafeIcon icon={Icons.Search} />
          <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search jobs, clients or engineers…" />
        </label>
        <select value={engineer} onChange={(event) => setEngineer(event.target.value)} className="engineer-filter" aria-label="Filter by engineer">
          <option value="all">All engineers</option>
          {data.engineers.map((person) => <option key={person.id} value={person.id}>{person.name}</option>)}
        </select>
        <select value={status} onChange={(event) => setStatus(event.target.value)} className="engineer-filter" aria-label="Filter by status">
          {STATUS_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select>
        {view === 'map' && (
          <div className="range-filter">
            <input type="date" value={range.from} onChange={(event) => setRange((r) => ({ ...r, from: event.target.value }))} aria-label="From date" />
            <span>→</span>
            <input type="date" value={range.to} onChange={(event) => setRange((r) => ({ ...r, to: event.target.value }))} aria-label="To date" />
          </div>
        )}
      </div>

      {requests.length > 0 && (
        <section className="requests-strip">
          <span className="requests-label">Client requests</span>
          {requests.map((job) => (
            <button type="button" key={job.id} className="request-chip" onClick={() => setEditingJob(job)}>
              <strong>{job.title}</strong>
              <span>{clientName(job.client_id)}</span>
              {job.status === 'reschedule_requested' && job.client_requested_date ? (
                <span className="request-chip-date">prefers {String(job.client_requested_date).slice(0, 10)}</span>
              ) : null}
              {job.status === 'requested' ? <span className="request-chip-tag">new request</span> : <span className="request-chip-tag">reschedule</span>}
            </button>
          ))}
          <span className="requests-hint">Click to schedule — giving a request a slot confirms it.</span>
        </section>
      )}

      {!data.jobs.length ? (
        <EmptyState title="No jobs yet" message="Create jobs from the Jobs area or a client's Work History tab to see them here." />
      ) : view === 'kanban' ? (
        <KanbanBoard jobs={filtered} clientName={clientName} onMove={moveJob} onEditJob={setEditingJob} />
      ) : view === 'calendar' ? (
        <ScheduleCalendar jobs={filtered} clientName={clientName} onEditJob={setEditingJob} />
      ) : (
        <JobsMap jobs={mapJobs} allJobs={data.jobs} clients={data.clients} clientName={clientName} onEditJob={setEditingJob} />
      )}

      {editingJob && (
        <JobForm open={Boolean(editingJob)} job={editingJob} onClose={() => setEditingJob(null)} />
      )}
    </div>
  );
}
